import { Helper } from 'Public_old/src/app/Common/Helpers/helper';
import { Component, ElementRef, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { Observable } from 'rxjs';
import { SpaContext } from '../../Classes/spacontext';
import { VisibaSaveButtonComponent } from '../../Common/Directives/visibaSaveButton';
import { AnswerApiModel } from '../../Services/api/v2/Models/AnswerApiModel';
import { BasketFormAnswer } from '../../Services/api/v2/Models/Basket/BasketFormAnswer';
import { FormGraphApiModel } from '../../Services/api/v2/Models/FormGraphApiModel';
import { PageApiModel } from '../../Services/api/v2/Models/PageApiModel';
import { QuestionApiModel } from '../../Services/api/v2/Models/QuestionApiModel';
import { DynamicFormServiceV2 } from '../../Services/api/v2/Units/dynamic-form.service';
import { DynamicFormAnswer } from './DynamicFormAnswer';
import { DynamicFormData } from './DynamicFormData';
import { DynamicFormOutcome } from './DynamicFormOutcome';

@Component({
	standalone: false,
	selector: 'dynamic-form',
	template: `
		<div *ngIf="form != null">
			<div class="row head">
				<h1>{{ form.Header }}</h1>
				<div class="description">
					{{ form.Description }}
				</div>
			</div>

			<div class="page">
				<form *ngIf="activePage != null" [class.ng-submitted]="activePage.submitted">
					<h2>{{ 'DynamicForms.Page' | translate }} {{ formMeta.flatPageIndex + 1 }}</h2>
					<div class="questions">
						<div
							class="question row"
							*ngFor="let q of activePage.Questions"
							[ngSwitch]="q.Type"
							[class.customValidation]="q.Type === 'MultiChoice' || q.Type === 'SingleChoiceRadio'"
						>
							<ng-template [ngSwitchCase]="'YesNo'">
								<dfq-yesno [formId]="form.Id" [data]="q" [readonly]="completed"></dfq-yesno>
							</ng-template>
							<ng-template [ngSwitchCase]="'MultiChoice'">
								<dfq-multichoice [formId]="form.Id" [data]="q" [readonly]="completed"></dfq-multichoice>
							</ng-template>
							<ng-template [ngSwitchCase]="'Multiline'">
								<dfq-multiline [formId]="form.Id" [data]="q" [readonly]="completed"></dfq-multiline>
							</ng-template>
							<ng-template [ngSwitchCase]="'SingleChoiceRadio'">
								<dfq-singlechoiceradio [formId]="form.Id" [data]="q" [readonly]="completed"></dfq-singlechoiceradio>
							</ng-template>
							<ng-template [ngSwitchCase]="'SingleChoiceSelect'">
								<div class="span24">
									<dfq-singlechoiceselect [formId]="form.Id" [data]="q" [readonly]="completed"></dfq-singlechoiceselect>
								</div>
							</ng-template>
						</div>
					</div>
				</form>

				<div class="navigation">
					<ng-template [ngIf]="activePage === null">
						<save-btn class="send" (click)="setActivePage()">{{ 'LicenceHolder.DynamicForm.Button.Start' | translate }}</save-btn>
					</ng-template>

					<ng-template [ngIf]="activePage !== null">
						<div class="pagination">
							<span>
								<span>{{ formMeta.flatPageIndex + 1 }}</span>
								{{ 'Common.Word.NavigationOf' | translate }}
								{{ formMeta.totalPages }}
							</span>
						</div>
						<div class="row">
							<div class="back" [style.opacity]="formMeta.flatPageIndex > 0 ? 100 : 0" (click)="back()">
								<visiba-svg a="ArrowLeft"></visiba-svg>
								{{ 'Common.Button.Back' | translate }}
							</div>
							<button *ngIf="formMeta.staticLastPage?.Id != activePage?.Id" class="btn next" (click)="continue()">
								{{ 'Common.Button.Next' | translate }}
							</button>
							<save-btn
								*ngIf="formMeta.staticLastPage?.Id == activePage?.Id && !completed"
								class="send"
								#finishbtn
								(click)="continue(finishbtn)"
								>{{ 'Common.Button.Next' | translate }}</save-btn
							>
						</div>
					</ng-template>
					<div class="abortLink" *ngIf="false && data.Delayable">
						<a (click)="emitOutcome('delayed')">{{ 'LicenceHolder.DynamicForm.Button.AnswerLater' | translate }}</a>
					</div>

					<div class="abortLink" *ngIf="false && !formMeta.isRequired">
						<a (click)="emitOutcome('skipped'); setCompletion()">{{ 'LicenceHolder.DynamicForm.Button.SkipForm' | translate }}</a>
					</div>
					<div class="abortLink" *ngIf="data.Abortable">
						<a (click)="emitOutcome('aborted')">{{ 'LicenceHolder.DynamicForm.Button.Abort' | translate }}</a>
					</div>
				</div>
			</div>
		</div>
	`,
	styleUrls: ['../../Css/Components/dynamicFormComponent.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class DynamicFormComponent {
	@Input() id: number = null;
	@Input() data: DynamicFormData;
	@Input() emitAnswers = false;
	@Output() onComplete = new EventEmitter<DynamicFormOutcome>();

	@Output() onNextPage = new EventEmitter<{ [key: number]: BasketFormAnswer[] }>();

	respondantId: number = null;
	form: FormGraphApiModel = null;
	formMeta: FormMeta = null;
	activePage: PageApiModel = null;
	public completed = false;
	private answers = [];

	constructor(
		private _dynamicFormService: DynamicFormServiceV2,
		// biome-ignore lint/correctness/noUnusedPrivateClassMembers: suppressed due to migration - should be fixed
		private _spaContext: SpaContext,
		private _elementRef: ElementRef,
		private readonly helper: Helper,
	) {}

	ngOnChanges() {
		this.formMeta = { currPage: 0, currPageGroup: 0, totalPages: null, flatPageIndex: 0, isRequired: true };
		this.form = null;
		this.activePage = null;
		this.respondantId = null;
		this.answers = [];
		this._dynamicFormService.get(this.id).subscribe(
			(response) => {
				this.form = response.data;

				// Remove empty PageGroups
				this.form.PageGroups = this.form.PageGroups.filter((x) => x.Pages.length > 0);

				this.formMeta.staticLastPage = this.getStaticLastPage();
				this.formMeta.isRequired = this.isRequired();

				let totalPages = 0;
				for (let i = 0; i < this.form.PageGroups.length; i++) totalPages += this.form.PageGroups[i].Pages.length;

				this.formMeta.totalPages = totalPages;

				let ind = 0;
				this.allQuestions().forEach((x) => {
					x.questionNumber = ++ind;
				});

				if (typeof this.data.RespondantId === 'number') {
					this.getRespondant();
				}
			},
			// biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration - should be fixed
			(error) => {},
		);
	}

	getRespondant() {
		this._dynamicFormService.getRespondant(this.data.RespondantId).subscribe(
			(response) => {
				if (response.data.Answers !== null) this.addAnswers(response.data.Answers);

				this.respondantId = response.data.Id;
				this.completed = response.data.Completed;
			},
			// biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration - should be fixed
			(error) => {},
		);
	}

	allQuestions(): QuestionApiModel[] {
		return this.form.PageGroups.map((x) => x.Pages)
			.reduce((a, b) => {
				return a.concat(b);
			}, [])
			.map((x) => x.Questions)
			.reduce((a, b) => {
				return a.concat(b);
			}, []);
	}

	addAnswers(answers: AnswerApiModel[]) {
		const questions = this.allQuestions();

		answers.forEach((a) => {
			const question = questions.filter((q) => q.Id === a.QuestionId);

			if (a.Choices && a.Choices.length > 0)
				// Verify choices still exist on question
				a.Choices = a.Choices.filter((x) => question[0].Options.find((o) => o.Id === x));

			if (question.length > 0) question[0].Answer = new DynamicFormAnswer(a, a.QuestionId, () => true);
		});
	}

	isRequired() {
		const questions: QuestionApiModel[] = this.allQuestions();

		return typeof questions.find((x) => x.Required) !== 'undefined';
	}

	scrollToStartOfPage() {
		const element = this._elementRef.nativeElement.querySelector('.row .head > h1');
		if (
			typeof element !== 'undefined' &&
			element !== null &&
			element.getBoundingClientRect().top + window.scrollY < window.pageYOffset
		) {
			element.scrollIntoView();
			window.scrollBy(0, -120);
		}
	}

	/**
	 * Get next availible page
	 */
	continue(btn: VisibaSaveButtonComponent = null) {
		this.scrollToStartOfPage();
		this.activePage.submitted = true;
		if (!this.pageIsValid(this.activePage)) return;

		if (btn !== null) btn.isLoading();

		const questions = this.activePage.Questions.filter(
			(x) => x.Answer.StringValue !== null || x.Answer.BoolValue !== null || x.Answer.IntValue !== null || x.Answer.Choices.length > 0,
		);

		if (questions.length === 0 || this.completed) {
			this.continueComplete();

			return;
		}

		this.submitPageAnswers(questions, this.activePage).subscribe(
			(ok) => {
				if (btn !== null) btn.isSuccess();

				this.continueComplete();
			},
			(nok) => {
				if (btn !== null) btn.isError();
			},
		);
	}

	continueComplete() {
		const nextPage = this.nextPage();
		if (nextPage !== null) {
			this.activePage = nextPage;
		} else {
			if (this.respondantId === null) {
				this.emitOutcome('completed');
			} else {
				this.setCompletion();
			}
		}
	}

	setCompletion() {
		if (this.respondantId !== null)
			this._dynamicFormService.setCompletion(this.respondantId).subscribe(
				(response) => {
					this.emitOutcome('completed');
				},
				// biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration - should be fixed
				(error) => {},
			);
	}

	back() {
		if (this.formMeta.flatPageIndex === 0) return;

		this.scrollToStartOfPage();
		this.activePage = this.prevPage();
		this.formMeta.flatPageIndex--;
	}

	getAnswersFromQuestions(questions: QuestionApiModel[]) {
		const answers = questions.map((x) => {
			x.Answer.Choices =
				x.Type === 'MultiChoice' || x.Type === 'SingleChoiceRadio' || x.Type === 'SingleChoiceSelect' ? x.Answer.Choices : null;

			return x.Answer;
		});

		this.answers.push(...answers);

		return this.answers;
	}

	/**
	 *
	 */
	submitPageAnswers(questions: QuestionApiModel[], page: PageApiModel): Observable<boolean> {
		return Observable.create((observer) => {
			const allAnswers = this.getAnswersFromQuestions(questions);
			allAnswers.forEach((answer) => {
				if (answer.StringValue) {
					answer.StringValue = this.helper.html.stripHtml(answer.StringValue);
				}
			});

			if (this.emitAnswers) {
				const formAnswers = {
					[this.form.Id]: allAnswers.map((answer) => {
						const p = new BasketFormAnswer();
						p.BoolAnswer = answer.BoolValue;
						p.IntValue = answer.IntValue;
						p.MultiAnswer = answer.Choices;
						p.QuestionId = answer.QuestionId;
						p.TextAnswer = answer.StringValue;

						return p;
					}),
				};

				this.onNextPage.emit(formAnswers);
				observer.next();
				observer.complete();
			} else if (this.respondantId === null) {
				// Cache answers
				observer.next();
				observer.complete();
			} else {
				this._dynamicFormService.answer(this.respondantId, allAnswers).subscribe(
					(response) => {
						observer.next(response);
					},
					(error) => observer.error(error),
					() => observer.complete(),
				);
			}
		});
	}

	setActivePage() {
		this.activePage = this.form.PageGroups[0].Pages[0];
	}

	/**
	 * Get next availible page
	 */
	nextPage(): PageApiModel {
		const fm = this.formMeta;
		const nav = { g: fm.currPageGroup, p: fm.currPage + 1 };
		let firstIt = true;

		for (let i = nav.g; i < this.form.PageGroups.length; i++) {
			const pageGroup = this.form.PageGroups[i];
			if (!firstIt) nav.p = 0;
			firstIt = false;

			// biome-ignore lint/correctness/noUnreachable: suppressed due to migration - should be fixed
			for (let j = nav.p; j < pageGroup.Pages.length; j++) {
				const page = pageGroup.Pages[j];
				fm.currPageGroup = i;
				fm.currPage = j;
				this.formMeta.flatPageIndex++;

				return page;
			}
		}

		return null;
	}

	prevPage(): PageApiModel {
		const fm = this.formMeta;
		//Todo: Has no constrint check
		fm.currPage--;

		if (fm.currPage < 0 && fm.currPageGroup > 0) {
			fm.currPageGroup--;
			fm.currPage = this.form.PageGroups[fm.currPageGroup].Pages.length - 1;
		}

		return this.form.PageGroups[fm.currPageGroup].Pages[fm.currPage];
	}

	/**
	 * Check if form has a static last page. A page that will always be shown.
	 * Return: That page
	 */
	getStaticLastPage(): PageApiModel {
		const group = this.form.PageGroups[this.form.PageGroups.length - 1];

		return group.Pages[group.Pages.length - 1];
	}

	/**
	 * Check if a page validates
	 */
	pageIsValid(page: PageApiModel): boolean {
		let result = true;
		page.Questions.forEach((q) => {
			result = result && q.Answer.Valid();
		});

		return result;
	}

	emitOutcome(outcome: DynamicFormOutcome) {
		this.onComplete.emit(outcome);
	}
}

interface FormMeta {
	currPageGroup: number;
	currPage: number;
	totalPages: number;
	flatPageIndex: number;
	isRequired: boolean;

	staticLastPage?: PageApiModel;
	formHasConstraints?: boolean;
}
